// Enhanced ADA Compliance Testing Rules
// Based on AccessScan functionality and WCAG 2.1 AA guidelines

export const ACCESSIBILITY_CATEGORIES = {
  GRAPHICS: 'Graphics',
  CLICKABLES: 'Clickables', 
  FORMS: 'Forms',
  READABILITY: 'Readability',
  TITLES: 'Titles',
  ORIENTATION: 'Orientation',
  MENUS: 'Menus',
  DOCUMENT: 'Document',
  CAROUSELS: 'Carousels',
  TABLES: 'Tables',
  GENERAL: 'General'
};

export const SEVERITY_LEVELS = {
  MILD: 'Mild',
  MODERATE: 'Moderate', 
  SEVERE: 'Severe'
};

export const COMPREHENSIVE_RULES = [
  // CLICKABLES CATEGORY
  {
    id: 1,
    category: ACCESSIBILITY_CATEGORIES.CLICKABLES,
    title: 'Elements that act as buttons but use non-button tags',
    description: 'Elements that act as buttons but use non-button tags (e.g., span, div, a) should include a role="button" attribute.',
    severity: SEVERITY_LEVELS.SEVERE,
    wcagGuideline: '4.1.2',
    selector: 'span[onclick], div[onclick], a[href="#"], a[href="javascript:void(0)"]',
    test: (element) => {
      const hasClickHandler = element.onclick || element.getAttribute('onclick');
      const isButtonLike = element.getAttribute('href') === '#' || 
                          element.getAttribute('href') === 'javascript:void(0)' ||
                          hasClickHandler;
      const hasButtonRole = element.getAttribute('role') === 'button';
      const isActualButton = element.tagName.toLowerCase() === 'button';
      
      return isButtonLike && !hasButtonRole && !isActualButton;
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('button, [role="button"]'));
    },
    message: 'Non-button element acting as button missing role="button" attribute'
  },

  {
    id: 2,
    category: ACCESSIBILITY_CATEGORIES.CLICKABLES,
    title: 'Buttons should include text explaining their functionality',
    description: 'Buttons should include text explaining their functionality, and if icons are used as buttons, a screen-reader only text or an "aria-label" attribute should be used for that description.',
    severity: SEVERITY_LEVELS.MODERATE,
    wcagGuideline: '4.1.2',
    selector: 'button, input[type="button"], input[type="submit"], [role="button"]',
    test: (element) => {
      const text = element.textContent?.trim() || '';
      const value = element.value?.trim() || '';
      const ariaLabel = element.getAttribute('aria-label')?.trim() || '';
      const title = element.getAttribute('title')?.trim() || '';
      
      return !text && !value && !ariaLabel && !title;
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('button, input[type="button"], input[type="submit"], [role="button"]'))
        .filter(el => {
          const text = el.textContent?.trim() || '';
          const value = el.value?.trim() || '';
          const ariaLabel = el.getAttribute('aria-label')?.trim() || '';
          const title = el.getAttribute('title')?.trim() || '';
          return text || value || ariaLabel || title;
        });
    },
    message: 'Button missing accessible name (text, aria-label, or title)'
  },

  {
    id: 3,
    category: ACCESSIBILITY_CATEGORIES.CLICKABLES,
    title: 'Links should include descriptive text',
    description: 'Links should include text that sets expectations about the page they lead to. If empty links are used as layout wrapping elements, provide a description using screen-reader only text or an "aria-label" attribute.',
    severity: SEVERITY_LEVELS.MODERATE,
    wcagGuideline: '2.4.4',
    selector: 'a[href]',
    test: (element) => {
      const text = element.textContent?.trim() || '';
      const ariaLabel = element.getAttribute('aria-label')?.trim() || '';
      const title = element.getAttribute('title')?.trim() || '';
      
      return !text && !ariaLabel && !title;
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('a[href]'))
        .filter(el => {
          const text = el.textContent?.trim() || '';
          const ariaLabel = el.getAttribute('aria-label')?.trim() || '';
          const title = el.getAttribute('title')?.trim() || '';
          return text || ariaLabel || title;
        });
    },
    message: 'Link missing accessible name (text, aria-label, or title)'
  },

  {
    id: 4,
    category: ACCESSIBILITY_CATEGORIES.CLICKABLES,
    title: 'Links that open in new tab should indicate this to users',
    description: 'Links that open in a new tab or a new window should either have an "aria-label" attribute or a screen-reader only element explaining to screen-readers that this opens in a new tab.',
    severity: SEVERITY_LEVELS.SEVERE,
    wcagGuideline: '3.2.5',
    selector: 'a[target="_blank"]',
    test: (element) => {
      const ariaLabel = element.getAttribute('aria-label') || '';
      const text = element.textContent || '';
      const hasNewTabIndicator = ariaLabel.toLowerCase().includes('new tab') ||
                                ariaLabel.toLowerCase().includes('new window') ||
                                text.toLowerCase().includes('new tab') ||
                                text.toLowerCase().includes('new window') ||
                                element.querySelector('.sr-only, .visually-hidden');
      
      return !hasNewTabIndicator;
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('a[target="_blank"]'))
        .filter(el => {
          const ariaLabel = el.getAttribute('aria-label') || '';
          const text = el.textContent || '';
          return ariaLabel.toLowerCase().includes('new tab') ||
                 ariaLabel.toLowerCase().includes('new window') ||
                 text.toLowerCase().includes('new tab') ||
                 text.toLowerCase().includes('new window') ||
                 el.querySelector('.sr-only, .visually-hidden');
        });
    },
    message: 'Link opens in new tab/window without indicating this to screen readers'
  },

  // TITLES CATEGORY
  {
    id: 5,
    category: ACCESSIBILITY_CATEGORIES.TITLES,
    title: 'Page should have exactly one H1 heading',
    description: 'An H1 heading provides information to screen-reader users about the main topic of the page. Each page should have exactly one H1 heading.',
    severity: SEVERITY_LEVELS.SEVERE,
    wcagGuideline: '1.3.1',
    selector: 'h1',
    test: (element, doc) => {
      const h1Elements = doc.querySelectorAll('h1');
      return h1Elements.length !== 1;
    },
    customTest: (doc) => {
      const h1Elements = doc.querySelectorAll('h1');
      if (h1Elements.length === 0) {
        return { hasViolation: true, message: 'Page missing required H1 heading' };
      }
      if (h1Elements.length > 1) {
        return { hasViolation: true, message: `Page has ${h1Elements.length} H1 headings, should have exactly one` };
      }
      return { hasViolation: false };
    },
    getSuccessfulElements: (doc) => {
      const h1Elements = doc.querySelectorAll('h1');
      return h1Elements.length === 1 ? Array.from(h1Elements) : [];
    },
    message: 'Page must have exactly one H1 heading'
  },

  {
    id: 6,
    category: ACCESSIBILITY_CATEGORIES.TITLES,
    title: 'HTML heading elements should contain text',
    description: 'HTML heading elements (h1-h6) should contain text content. If they include images or links, those elements must provide alternative text or screen-reader-only text.',
    severity: SEVERITY_LEVELS.MODERATE,
    wcagGuideline: '1.3.1',
    selector: 'h1, h2, h3, h4, h5, h6',
    test: (element) => {
      const text = element.textContent?.trim() || '';
      if (text) return false; // Has text, no violation
      
      // Check for images with alt text
      const images = element.querySelectorAll('img[alt]');
      if (images.length > 0) return false; // Has images with alt text
      
      // Check for aria-label
      if (element.getAttribute('aria-label')?.trim()) return false;
      
      return true; // Empty heading
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('h1, h2, h3, h4, h5, h6'))
        .filter(el => {
          const text = el.textContent?.trim() || '';
          const hasImagesWithAlt = el.querySelectorAll('img[alt]').length > 0;
          const hasAriaLabel = el.getAttribute('aria-label')?.trim();
          return text || hasImagesWithAlt || hasAriaLabel;
        });
    },
    message: 'Heading element is empty or lacks accessible content'
  },

  {
    id: 7,
    category: ACCESSIBILITY_CATEGORIES.TITLES,
    title: 'Elements functioning as headings should use heading tags',
    description: 'Elements that visually function as headings but are not coded with a heading tag should include a role="heading" attribute or be updated to use the correct heading tag',
    severity: SEVERITY_LEVELS.MODERATE,
    wcagGuideline: '1.3.1',
    selector: 'p, div, span',
    test: (element) => {
      const text = element.textContent?.trim() || '';
      const style = window.getComputedStyle ? window.getComputedStyle(element) : {};
      const fontSize = parseFloat(style.fontSize) || 0;
      const fontWeight = style.fontWeight;
      
      // Heuristic: large, bold text might be a heading
      const looksLikeHeading = (fontSize >= 20 || fontWeight === 'bold' || fontWeight >= 600) && 
                              text.length > 0 && text.length < 100;
      
      const hasHeadingRole = element.getAttribute('role') === 'heading';
      const isActualHeading = /^h[1-6]$/i.test(element.tagName);
      
      return looksLikeHeading && !hasHeadingRole && !isActualHeading;
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('[role="heading"], h1, h2, h3, h4, h5, h6'));
    },
    message: 'Element appears to be a heading but lacks proper heading markup or role'
  },

  {
    id: 8,
    category: ACCESSIBILITY_CATEGORIES.TITLES,
    title: 'Heading levels should follow logical hierarchy',
    description: 'Heading levels should not be skipped and must follow a consistent hierarchy. For example, you should not use h2 and h4 headings without including an h3 in between.',
    severity: SEVERITY_LEVELS.MODERATE,
    wcagGuideline: '1.3.1',
    customTest: (doc) => {
      const headings = Array.from(doc.querySelectorAll('h1, h2, h3, h4, h5, h6'))
        .map(h => ({
          element: h,
          level: parseInt(h.tagName.charAt(1))
        }))
        .sort((a, b) => a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

      const violations = [];
      let previousLevel = 0;

      headings.forEach(heading => {
        if (heading.level > previousLevel + 1) {
          violations.push({
            element: heading.element,
            message: `Heading level ${heading.level} skips level ${previousLevel + 1}`
          });
        }
        previousLevel = Math.max(previousLevel, heading.level);
      });

      return violations.length > 0 ? { hasViolation: true, violations } : { hasViolation: false };
    },
    getSuccessfulElements: (doc) => {
      // Return headings that follow proper hierarchy
      const headings = Array.from(doc.querySelectorAll('h1, h2, h3, h4, h5, h6'));
      return headings; // Simplified for now
    },
    message: 'Heading hierarchy is not logical (levels are skipped)'
  },

  // FORMS CATEGORY
  {
    id: 9,
    category: ACCESSIBILITY_CATEGORIES.FORMS,
    title: 'Interactive elements must be keyboard navigable',
    description: 'Interactive elements such as links, buttons and form fields should all be navigable using the keyboard by either using a focusable element (a, button, input, etc.) or including the "tabindex" attribute that equals to "0".',
    severity: SEVERITY_LEVELS.SEVERE,
    wcagGuideline: '2.1.1',
    selector: '[onclick], [role="button"]:not(button):not(input):not(a), [role="link"]:not(a)',
    test: (element) => {
      const tagName = element.tagName.toLowerCase();
      const nativelyFocusable = ['a', 'button', 'input', 'select', 'textarea', 'iframe'].includes(tagName);
      const tabindex = element.getAttribute('tabindex');
      const hasTabindex = tabindex !== null && parseInt(tabindex) >= 0;
      
      return !nativelyFocusable && !hasTabindex;
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('a, button, input, select, textarea, [tabindex="0"]'));
    },
    message: 'Interactive element is not keyboard accessible (missing tabindex="0")'
  },

  {
    id: 10,
    category: ACCESSIBILITY_CATEGORIES.FORMS,
    title: 'Interactive elements should have visible focus indicators',
    description: 'Interactive elements that can be navigated using the keyboard should be surrounded by a visual outline whenever they are focused.',
    severity: SEVERITY_LEVELS.MODERATE,
    wcagGuideline: '2.4.7',
    selector: 'a, button, input, select, textarea, [tabindex="0"]',
    test: (element) => {
      // This would ideally check computed focus styles, but that's complex in static analysis
      // For now, we'll check for common focus-hiding patterns
      const style = element.style.outline;
      const hasOutlineNone = style && (style.includes('none') || style === '0');
      
      return hasOutlineNone;
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('a:focus, button:focus, input:focus, select:focus, textarea:focus'))
        .filter(el => {
          const style = el.style.outline;
          return !style || (!style.includes('none') && style !== '0');
        });
    },
    message: 'Interactive element has focus indicator disabled (outline: none)'
  },

  // GRAPHICS CATEGORY  
  {
    id: 18,
    category: ACCESSIBILITY_CATEGORIES.GRAPHICS,
    title: 'Images should have alternative text descriptions',
    description: 'Images should have an alternative text description that describes both the objects and the embedded text that the image contains, using the "alt" attribute.',
    severity: SEVERITY_LEVELS.SEVERE,
    wcagGuideline: '1.1.1',
    selector: 'img',
    test: (element) => {
      const alt = element.getAttribute('alt');
      const ariaLabel = element.getAttribute('aria-label');
      const ariaHidden = element.getAttribute('aria-hidden') === 'true';
      const role = element.getAttribute('role');
      
      // If aria-hidden="true" or role="presentation"/"none", alt not required
      if (ariaHidden || role === 'presentation' || role === 'none') {
        return false;
      }
      
      // Check if alt attribute exists (can be empty for decorative images)
      return alt === null && !ariaLabel;
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('img[alt], img[aria-label], img[aria-hidden="true"], img[role="presentation"], img[role="none"]'));
    },
    message: 'Image missing alt attribute or aria-label'
  },

  {
    id: 19,
    category: ACCESSIBILITY_CATEGORIES.GRAPHICS,
    title: 'Background images should have proper roles when meaningful',
    description: 'Background images that are not just for decoration purposes should have the same treatment as standard images. They should include a role="img" attribute and an alternative text description using an "aria-label" attribute.',
    severity: SEVERITY_LEVELS.MODERATE,
    wcagGuideline: '1.1.1',
    selector: '[style*="background-image"], [class*="bg-"], [class*="background"]',
    test: (element) => {
      const style = element.style.backgroundImage || '';
      const className = element.className || '';
      const hasBackgroundImage = style.includes('url(') || 
                                 className.includes('bg-') || 
                                 className.includes('background');
      
      if (!hasBackgroundImage) return false;
      
      const role = element.getAttribute('role');
      const ariaLabel = element.getAttribute('aria-label');
      const hasTextContent = element.textContent?.trim();
      
      // If element has text content, background is likely decorative
      if (hasTextContent) return false;
      
      // Check if it's marked as decorative or has proper img role
      return role !== 'presentation' && role !== 'none' && role !== 'img' && !ariaLabel;
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('[role="img"][aria-label], [role="presentation"], [role="none"]'))
        .filter(el => {
          const style = el.style.backgroundImage || '';
          const className = el.className || '';
          return style.includes('url(') || className.includes('bg-') || className.includes('background');
        });
    },
    message: 'Meaningful background image missing role="img" and aria-label'
  },

  {
    id: 20,
    category: ACCESSIBILITY_CATEGORIES.GRAPHICS,
    title: 'Decorative icons should be hidden from screen readers',
    description: 'Font icons, SVGs, or images used as spacers, decorations, or when their purpose is already described by other content, should include a role="presentation" or role="none" attribute.',
    severity: SEVERITY_LEVELS.MILD,
    wcagGuideline: '1.1.1',
    selector: 'i[class*="fa-"], i[class*="icon-"], svg, .icon',
    test: (element) => {
      const role = element.getAttribute('role');
      const ariaHidden = element.getAttribute('aria-hidden') === 'true';
      const ariaLabel = element.getAttribute('aria-label');
      const hasText = element.textContent?.trim();
      
      // If it has descriptive text or aria-label, it's not decorative
      if (hasText || ariaLabel) return false;
      
      // Check if it's properly marked as decorative
      return !ariaHidden && role !== 'presentation' && role !== 'none';
    },
    getSuccessfulElements: (doc) => {
      return Array.from(doc.querySelectorAll('[aria-hidden="true"], [role="presentation"], [role="none"]'));
    },
    message: 'Decorative icon not hidden from screen readers (missing aria-hidden="true" or role="presentation")'
  },

  // READABILITY CATEGORY
  {
    id: 35,
    category: ACCESSIBILITY_CATEGORIES.READABILITY,
    title: 'Font sizes should be at least 11px',
    description: 'Font sizes should be at least 11px to ensure readability across most font families. This requirement should also be verified when using dynamic units like REM or percentages.',
    severity: SEVERITY_LEVELS.MILD,
    wcagGuideline: '1.4.4',
    selector: '*',
    test: (element) => {
      if (!window.getComputedStyle) return false;
      
      const style = window.getComputedStyle(element);
      const fontSize = parseFloat(style.fontSize);
      const hasText = element.textContent?.trim();
      
      return hasText && fontSize < 11;
    },
    getSuccessfulElements: (doc) => {
      if (!window.getComputedStyle) return [];
      
      return Array.from(doc.querySelectorAll('*'))
        .filter(el => {
          const style = window.getComputedStyle(el);
          const fontSize = parseFloat(style.fontSize);
          const hasText = el.textContent?.trim();
          return hasText && fontSize >= 11;
        });
    },
    message: 'Text size is below minimum readable size (11px)'
  },

  {
    id: 37,
    category: ACCESSIBILITY_CATEGORIES.READABILITY,
    title: 'Text should have sufficient color contrast',
    description: 'Text elements should meet a minimum contrast ratio of 4.5:1 between the foreground (text color) and the background.',
    severity: SEVERITY_LEVELS.SEVERE,
    wcagGuideline: '1.4.3',
    selector: 'p, span, div, h1, h2, h3, h4, h5, h6, a, button, label',
    test: (element) => {
      // This is a simplified test - full contrast checking requires complex color calculations
      const hasText = element.textContent?.trim();
      if (!hasText) return false;
      
      if (!window.getComputedStyle) return false;
      
      const style = window.getComputedStyle(element);
      const color = style.color;
      const backgroundColor = style.backgroundColor;
      
      // Simple heuristics for common problematic patterns
      const isLightGrayText = color && (color.includes('rgb(192') || color.includes('#ccc') || color.includes('#ddd'));
      const isWhiteBackground = backgroundColor && (backgroundColor.includes('rgb(255, 255, 255)') || backgroundColor.includes('#fff'));
      
      return isLightGrayText && isWhiteBackground;
    },
    getSuccessfulElements: (doc) => {
      // Return elements that don't match the problematic patterns
      return Array.from(doc.querySelectorAll('p, span, div, h1, h2, h3, h4, h5, h6, a, button, label'))
        .filter(el => {
          if (!window.getComputedStyle) return true;
          const style = window.getComputedStyle(el);
          const color = style.color;
          const isLightGrayText = color && (color.includes('rgb(192') || color.includes('#ccc') || color.includes('#ddd'));
          return !isLightGrayText;
        });
    },
    message: 'Text color may not have sufficient contrast with background'
  },

  // DOCUMENT CATEGORY
  {
    id: 31,
    category: ACCESSIBILITY_CATEGORIES.DOCUMENT,
    title: 'Page should have a descriptive title',
    description: 'The title tag in the head section should be present and describe the current webpage accurately. This title appears in the browser tab, search engine results, and helps screen-reader users understand the page content.',
    severity: SEVERITY_LEVELS.SEVERE,
    wcagGuideline: '2.4.2',
    customTest: (doc) => {
      const titleElement = doc.querySelector('title');
      const title = titleElement?.textContent?.trim();
      
      if (!title) {
        return { hasViolation: true, message: 'Page missing title element' };
      }
      
      if (title.length < 10 || title.toLowerCase() === 'untitled' || title.toLowerCase().includes('new page')) {
        return { hasViolation: true, message: 'Page title is not descriptive' };
      }
      
      return { hasViolation: false };
    },
    getSuccessfulElements: (doc) => {
      const titleElement = doc.querySelector('title');
      const title = titleElement?.textContent?.trim();
      return title && title.length >= 10 ? [titleElement] : [];
    },
    message: 'Page title is missing or not descriptive'
  },

  {
    id: 32,
    category: ACCESSIBILITY_CATEGORIES.DOCUMENT,
    title: 'HTML should specify language',
    description: 'The HTML tag should include a "lang" attribute representing the main language of the webpage to help screen-readers provide accurate pronunciation.',
    severity: SEVERITY_LEVELS.MODERATE,
    wcagGuideline: '3.1.1',
    customTest: (doc) => {
      const htmlElement = doc.documentElement || doc.querySelector('html');
      const lang = htmlElement?.getAttribute('lang')?.trim();
      
      if (!lang) {
        return { hasViolation: true, message: 'HTML element missing lang attribute' };
      }
      
      // Basic validation of language code format
      if (!/^[a-z]{2}(-[A-Z]{2})?$/i.test(lang)) {
        return { hasViolation: true, message: 'HTML lang attribute has invalid format' };
      }
      
      return { hasViolation: false };
    },
    getSuccessfulElements: (doc) => {
      const htmlElement = doc.documentElement || doc.querySelector('html');
      const lang = htmlElement?.getAttribute('lang')?.trim();
      return lang && /^[a-z]{2}(-[A-Z]{2})?$/i.test(lang) ? [htmlElement] : [];
    },
    message: 'HTML element missing or invalid lang attribute'
  }
];

// Helper function to calculate accessibility score
export const calculateScore = (successes, failures) => {
  const total = successes + failures;
  if (total === 0) return 0;
  return Math.round((successes / total) * 100);
};

// Helper function to generate CSS selector for an element
export const generateElementSelector = (element) => {
  if (!element || !element.tagName) return '';
  
  try {
    const parts = [];
    let current = element;
    
    while (current && current.nodeType === 1 && current.tagName.toLowerCase() !== 'html') {
      let part = current.tagName.toLowerCase();
      
      if (current.id) {
        part += `#${current.id}`;
        parts.unshift(part);
        break;
      }
      
      const className = current.className;
      if (className && typeof className === 'string') {
        const firstClass = className.split(' ').filter(Boolean)[0];
        if (firstClass) part += `.${firstClass}`;
      }
      
      // Add nth-of-type if needed for uniqueness
      let sibling = current;
      let nth = 1;
      while (sibling = sibling.previousElementSibling) {
        if (sibling.tagName === current.tagName) nth++;
      }
      if (nth > 1) part += `:nth-of-type(${nth})`;
      
      parts.unshift(part);
      current = current.parentElement;
    }
    
    return parts.join(' > ');
  } catch (error) {
    return element.tagName?.toLowerCase() || '';
  }
};

export default COMPREHENSIVE_RULES;